<template>
  <div class="sidebar-container">
    <div
      class="clear-questions"
      :class="{ disabled: needsFirstSelection }"
    >
      <button
        :disabled="needsFirstSelection"
        @click="clearQuestions()"
      >
        Clear all selections <i class="bi bi-x-circle-fill" />
      </button>
    </div>
    <sidebar-topic
      :step="1"
      title="Primary Question"
      description="Select the primary survey question for the analysis. The results will show the distribution of this question's response categories."
      :disabled="isQuestionDisabled"
      :selection-required="true"
      :selected-topic="selectedRowQuestion"
      @onSelectTopic="selectFirst($event)"
    />
    <div
      class="swap-questions"
      :class="{ disabled: needsFirstSelection || needsSecondSelection }"
    >
      <button
        :disabled="needsFirstSelection || needsSecondSelection"
        @click="swapQuestions()"
      >
        Swap primary/comparison questions
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="swap-icon"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="m8 0c-4.418 0-8 3.582-8 8 0 4.418 3.582 8 8 8 4.418 0 8-3.582 8-8 0-4.418-3.582-8-8-8zm-2.162 4.004c0.25 0 0.5 0.1667 0.5 0.5v5.783l1.398-1.396c0.472-0.472 1.181 0.2374 0.709 0.709l-2.254 2.248c-0.1954 0.1959-0.5117 0.1959-0.707 0l-2.227-2.275c-0.472-0.472 0.235-1.179 0.707-0.707l1.373 1.422v-5.783c0-0.3333 0.25-0.5 0.5-0.5zm4.299 0c0.1282 0 0.2558 0.04856 0.3535 0.1465l2.254 2.25c0.4725 0.4715-0.237 1.179-0.709 0.707l-1.398-1.396v5.783c0 0.6667-1 0.6667-1 0v-5.783l-1.373 1.422c-0.472 0.472-1.179-0.235-0.707-0.707l2.227-2.275c0.09768-0.09793 0.2254-0.1465 0.3535-0.1465z" />
        </svg>
      </button>
    </div>
    <sidebar-topic
      :step="2"
      title="Comparison Question"
      description="To compare the results by another survey question, select the comparison question. The results will show the distribution of this question's response categories within each response category of the primary question."
      :disabled="isComparisonDisabled"
      :selected-topic="selectedColumnQuestion"
      @onSelectTopic="selectSecond($event)"
    />
    <sidebar-topic
      :step="3"
      title="Population Filter"
      description="To narrow the analysis to a subpopulation, select a survey question and one or more of its response categories. The results will be filtered to include only the patients in one of these categories. The filter is limited to one question at a time; you cannot combine categories from multiple questions into a single filter."
      :disabled="isFilterDisabled"
      :selected-topic="selectedFilterQuestion"
      :selected-levels="filterByQuestion"
      :use-sub-selection="true"
      @onSelectTopic="selectFilter($event)"
    />
  </div>
</template>

<script>
import SidebarTopic from './SidebarTopic.vue'
import { Hs } from '@rtidatascience/harness'
import { mapGetters } from 'vuex'

export default {
  name: 'SidebarComponent',
  components: { SidebarTopic },
  methods: {
    selectFirst ({ id, levels }) {
      this.$store.commit('buildFilter', levels)
      this.$store.commit('setRowQuestion')

      const hs = new Hs('topic', this.$store)
      hs.loadData()
    },
    selectSecond ({ id, levels }) {
      this.$store.commit('buildFilter', levels)
      this.$store.commit('setColumnQuestion')

      const hs = new Hs('topic', this.$store)
      hs.loadData()
    },
    selectFilter ({ id, levels }) {
      this.$store.commit('setFilterQuestion', { id, values: levels })

      const hs = new Hs('topic', this.$store)
      hs.loadData()
    },
    swapQuestions () {
      this.$store.commit('swapSelectedQuestions')

      const hs = new Hs('topic', this.$store)
      hs.loadData()
    },
    clearQuestions () {
      this.selectFirst({ id: null, levels: null })
      this.selectSecond({ id: null, levels: null })
      this.selectFilter({ id: null, levels: null })
    }
  },
  computed: {
    ...mapGetters(['rowQuestion', 'columnQuestion', 'filterQuestion', 'filterByQuestion', 'getQuestion']),
    selectedRowQuestion () {
      return this.rowQuestion
    },
    selectedColumnQuestion () {
      return this.columnQuestion
    },
    selectedFilterQuestion () {
      return this.filterQuestion
    },
    selectedFilterValues () {
      return this.filterByQuestion
    },
    needsFirstSelection () {
      return this.selectedRowQuestion == null
    },
    needsSecondSelection () {
      return this.selectedColumnQuestion == null
    },
    isQuestionDisabled () {
      return false
    },
    isComparisonDisabled () {
      return this.needsFirstSelection
    },
    isFilterDisabled () {
      return this.needsFirstSelection
    }
  }
}
</script>

<style lang="scss" scoped>
.sidebar-container {
  width: 100%;
  height: 100%;
}

.swap-questions, .clear-questions {
  text-align: right;
  padding: 4px 12px;
  background-color: #edf1f7;
  font-size: 1.1em;

  &.disabled {
    background-color: #f8f9fc;
  }

  button {
    border: 0;
    background: #edf1f7;
    transition: 300ms ease all;

    i, .swap-icon {
      color: #267cb8;
      transform: scale(1.2);
      transform-origin: left;
      display: inline-block;
      transition: 300ms ease all;
    }

    .swap-icon {
      position: relative;
      top: -0.15em;
      width: 1em;
      height: 1em;
    }

    &:hover {
      color: #267cb8;

      i, .swap-icon {
        color: #205492;
        transform: scale(1.45);
      }
    }

    &:disabled {
      background-color: #f8f9fc;
      color: #737373;

      i, .swap-icon {
        color: #aaa;
        transform: scale(1);
      }
    }
  }
}
</style>
